import { AXNUIFormPanelHeaderContainer, AXNUIRetryMessageContainer } from "@iddataweb/idw-common-ui/containers";
import { LocaleTypes } from "@iddataweb/idw-common-ui/enums";
import { AXNUIContent, AXNUILayout, AXNUITheme } from "@iddataweb/idw-common-ui/types";
import { PreviewEntityTypes } from "@enums/preview-entity-types";
import { FormattedRpServiceConfigurationAttribute } from "@idw-types/data/rp-service-configuration";
import { AXNUIThemingPreviewConfigurationState } from "@idw-types/common/axnui/axnui-theming-preview-configuration-state";
import { AXNUIThemingFormButtonContainer } from "../axnui-theming-form-button-container/axnui-theming-form-button-container";
import { AXNUIThemingAttributeInputList } from "../axnui-theming-attribute-input-list/axnui-theming-attribute-input-list";
import { AXNUIThemingPreFormHelpTextContainer } from "../axnui-theming-pre-form-help-text-container/axnui-theming-pre-form-help-text-container";
import { AXNUIThemingPostFormHelpTextContainer } from "../axnui-theming-post-form-help-text-container/axnui-theming-post-form-help-text-container";
import { AXNUIThemingPostSubmitHelpTextContainer } from "../axnui-theming-post-submit-help-text-container/axnui-theming-post-submit-help-text-container";
import { AXNUIThemingAsyncModal } from "../axnui-theming-async-modal/axnui-theming-async-modal";
import styles from "./axnui-theming-verification-form.less";
import { AXNUIThemingTabTypes } from "@enums/axnui-theming-tab-types";
import { useMemo } from "react";
import { AxnAttributeTypeField, AxnAttributeTypeFieldCustomization } from "@idw-types/data/reference-data/axn";

type AXNUIThemingVerificationFormProps = {
    axnuiContent: AXNUIContent;
    axnuiLayout: AXNUILayout;
    axnuiTheme: AXNUITheme;
    attributes: Array<FormattedRpServiceConfigurationAttribute>;
    axnAttributeTypeFields: Array<AxnAttributeTypeField>;
    axnAttributeTypeFieldCustomizations: Array<AxnAttributeTypeFieldCustomization>;
    localizeStrings: boolean;
    mergeThemingAndLocalizedFile: boolean;
    activeLanguage: LocaleTypes;
    hasCancelButtonURLEnabled: boolean;
    previewEntityType: PreviewEntityTypes;
    previewConfigurationState: AXNUIThemingPreviewConfigurationState;
    currentTab: AXNUIThemingTabTypes;
    setPreviewConfigurationState: (state: AXNUIThemingPreviewConfigurationState) => void;
};

export const AXNUIThemingVerificationForm = (props: AXNUIThemingVerificationFormProps) => {
    const {
        axnuiContent,
        axnuiLayout,
        axnuiTheme,
        attributes,
        axnAttributeTypeFields,
        axnAttributeTypeFieldCustomizations,
        localizeStrings,
        mergeThemingAndLocalizedFile,
        activeLanguage,
        hasCancelButtonURLEnabled,
        previewEntityType,
        previewConfigurationState,
        currentTab,
        setPreviewConfigurationState
    } = props;

    const shouldShowRetryMessages = useMemo(() => {
        return [AXNUIThemingTabTypes.POLICY_RETRY_MESSAGE].includes(currentTab);
    }, [currentTab]);
    return (
        <div className={styles.verificationForm}>
            <div className={styles.attributeInputListContainer}>
                <AXNUIFormPanelHeaderContainer
                    showTitle={true}
                    showDescription={true}
                    formPanelContent={axnuiContent.formPanel}
                    formPanelLayout={axnuiLayout.formPanel}
                    formPanelTheme={axnuiTheme.formPanel}
                    idpName={null}
                    logoPosition={null}
                    logoUrl={null}
                    relyingPartyName={null}
                    useThemedUI={true}
                />
                {shouldShowRetryMessages && (
                    <AXNUIRetryMessageContainer
                        axnuiContent={axnuiContent}
                        backgroundColor={axnuiTheme.policyRetryMessage.backgroundColor}
                        borderColor={axnuiTheme.policyRetryMessage.borderColor}
                        font={axnuiTheme.policyRetryMessage.font}
                        borderWidth={axnuiTheme.policyRetryMessage.borderWidth}
                        cornerRadius={axnuiTheme.policyRetryMessage.cornerRadius}
                        fontColor={axnuiTheme.policyRetryMessage.fontColor}
                        useThemedUI={true}
                    />
                )}
                {previewEntityType === PreviewEntityTypes.VERIFICATION_SERVICE && (
                    <AXNUIThemingPreFormHelpTextContainer
                        axnuiContent={axnuiContent}
                        axnuiLayout={axnuiLayout}
                        axnuiTheme={axnuiTheme}
                    />
                )}
                <AXNUIThemingAttributeInputList
                    attributes={attributes}
                    axnuiContent={axnuiContent}
                    axnAttributeTypeFields={axnAttributeTypeFields}
                    axnAttributeTypeFieldCustomizations={axnAttributeTypeFieldCustomizations}
                    localizeStrings={localizeStrings}
                    mergeThemingAndLocalizedFile={mergeThemingAndLocalizedFile}
                    activeLanguage={activeLanguage}
                    showAfterCapturePreview={previewConfigurationState.showAfterCapturePreview}
                    previewEntityType={previewEntityType}
                />
                {previewEntityType === PreviewEntityTypes.VERIFICATION_SERVICE && (
                    <AXNUIThemingPostFormHelpTextContainer
                        axnuiContent={axnuiContent}
                        axnuiLayout={axnuiLayout}
                        axnuiTheme={axnuiTheme}
                    />
                )}
                <AXNUIThemingFormButtonContainer
                    axnuiContent={axnuiContent}
                    axnuiTheme={axnuiTheme}
                    showAsyncButton={previewConfigurationState.showAsyncButton}
                    hasCancelButtonURLEnabled={hasCancelButtonURLEnabled}
                />
                {previewEntityType === PreviewEntityTypes.VERIFICATION_SERVICE && (
                    <AXNUIThemingPostSubmitHelpTextContainer
                        axnuiContent={axnuiContent}
                        axnuiLayout={axnuiLayout}
                        axnuiTheme={axnuiTheme}
                    />
                )}
                {previewConfigurationState.showAsyncModal && (
                    <AXNUIThemingAsyncModal
                        axnuiContent={axnuiContent}
                        previewConfigurationState={previewConfigurationState}
                        setPreviewConfigurationState={setPreviewConfigurationState}
                    />
                )}
            </div>
        </div>
    );
};